import { useQueries } from '@tanstack/react-query';
import { httpGet } from '$core/helpers/xhr';
import { useGlobalContext } from '../../contexts';

type AvailableCategory =
  | 'piroli'
  | 'mentoniere'
  | 'cordiere'
  | 'viti'
  | 'speciali'
  | 'servizi';

const CATEGORIES: AvailableCategory[] = ['piroli', 'mentoniere', 'cordiere', 'viti', 'speciali', 'servizi'];

const buildStoreFilter = (storeUuid: string | null): string => (
  storeUuid ? `&filter[stores.meta.drupal_internal__target_id]=${storeUuid}` : ''
);

const buildProductUrl = (
  productType: string,
  selectedStrumento: string,
  storeFilter: string,
): string => {
  if (productType === 'piroli' || productType === 'speciali') {
    return `/commerce_product/${productType}`
      + '?include=field_image,variations,field_legno,field_modello,field_strumento,field_finiture'
      + `&filter[field_strumento.name]=${selectedStrumento}`
      + `&filter[status]=1${storeFilter}`;
  }

  if (productType === 'cordiere' && selectedStrumento !== 'violoncello') {
    return `/commerce_product/${productType}`
      + '?include=field_image,variations,field_legno,field_modello,field_strumento,field_finiture'
      + `&filter[field_strumento.name]=${selectedStrumento}`
      + `&filter[status]=1${storeFilter}`;
  }

  if (productType === 'cordiere' && selectedStrumento === 'violoncello') {
    return '/commerce_product/cordiera_cello'
      + '?include=field_image,variations,field_legno, field_strumento'
      + `&filter[field_strumento.name]=${selectedStrumento}`
      + '&filter[langcode]=it'
      + `&filter[status]=1${storeFilter}`;
  }

  if (productType === 'mentoniere') {
    return `/commerce_product/${productType}`
      + '?include=field_image,variations,field_legno, field_modello, field_strumento'
      + `&filter[field_strumento.name]=${selectedStrumento}`
      + '&filter[langcode]=it'
      + `&filter[status]=1${storeFilter}`;
  }

  if (productType === 'servizi') {
    return `/commerce_product/${productType}`
      + '?include=field_image,variations, field_strumento'
      + `&filter[field_strumento.name]=${selectedStrumento}`
      + '&filter[langcode]=it'
      + `&filter[status]=1${storeFilter}`;
  }

  return '/commerce_product/viti'
    + '?include=field_immagini,field_immagini.field_media_image,variations,field_metallo,field_strumento,field_modello'
    + `&filter[field_strumento.name]=${selectedStrumento}`
    + '&filter[langcode]=it'
    + `&filter[status]=1${storeFilter}`;
};

type UseGetAvailableCategoriesResult = {
  availableCategories: AvailableCategory[];
  loading: boolean;
};

const useGetAvailableCategories = (selectedStrumento: string): UseGetAvailableCategoriesResult => {
  const { storeUuid } = useGlobalContext();
  const storeFilter = buildStoreFilter(storeUuid);
  const enabled = !!storeUuid && !!selectedStrumento;

  const results = useQueries({
    queries: CATEGORIES.map((cat) => ({
      queryKey: ['availableCategory', cat, selectedStrumento, storeUuid],
      queryFn: async () => {
        const url = buildProductUrl(cat, selectedStrumento, storeFilter);
        const response = await httpGet(url);
        const res = response.data?.data;
        return Array.isArray(res) && res.length > 0;
      },
      enabled,
    })),
  });

  const loading = results.some((r) => r.isLoading);
  const availableCategories = CATEGORIES.filter((_, i) => results[i].data === true);

  return { availableCategories, loading };
};

export { useGetAvailableCategories };
export type { AvailableCategory };
